/**
 * 文档管理 API
 *
 * DocumentProject 项目级 CRUD 已废弃（Task 9.2 / D8）；
 * 文件夹 → project.ts；条目 → work-item.ts；业务操作入参为 work_item_id。
 */
import { TaskAssignmentType, TaskPurpose, TaskType } from '@/api/common-types'
import type {
  FileUploadResponse,
  PaginatedResponse,
  PaginationParams,
  UserSummary,
} from '@/api/common-types'
import {
  getWorkItem,
} from '@/modules/project/api/work-item'
import { getCorrectedApiOrigin } from '@/shared/api/api-base'
import { request } from '@/shared/api/request'
import { fileProxyUrl, normalizeLegacyFileUrl } from '@/shared/utils/file-url'

/** @deprecated 使用 LegacyDocumentProjectStatus 或 WorkItem 派生 status */
export enum LegacyDocumentProjectStatus {
  DRAFT = 'draft',
  PENDING = 'pending',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

export { TaskAssignmentType, TaskPurpose, TaskType }
/** @deprecated 使用 LegacyDocumentProjectStatus */
export { LegacyDocumentProjectStatus as ProjectStatus }

// 新增的文档翻译相关类型
export interface DocumentSentence {
  id: number
  sentence_order: number
  sentence_type: 'text' | 'table'
  source_text: string
  target_text: string
  is_modified: boolean
  modified_at?: string
}

export interface SentenceUpdate {
  sentence_id: number
  target_text: string
}

export interface SegmentResponse {
  message: string
  count: number
}

export interface SentencesResponse {
  page: number
  limit: number
  total: number
  data: DocumentSentence[]
}

export interface SyncToPreviewResponse {
  success: boolean
  document_url: string
  pdf_url: string
  message: string
}

export interface ExportResponse {
  success: boolean
  download_url: string
  file_name: string
  message: string
}

// 文档条目视图（兼容旧 DocumentProject 消费方，Task 9.4 起逐步移除）
/** @deprecated 请使用 WorkItem；此类型仅供过渡兼容 */
export interface DocumentProject {
  id: number
  /** 所属文件夹 project_id（WorkItem.project_id）；热词等项目级资源用此字段 */
  project_id?: number
  title: string
  document_url?: string
  document_type: string
  document_size?: number
  original_language?: string
  target_languages: string[]
  translated_document_url?: string
  translation_progress?: any
  status: LegacyDocumentProjectStatus | string
  /** @deprecated 使用 TaskAssignmentType */
  task_type?: TaskAssignmentType | TaskType
  creator_id: number
  assignee_id?: number
  modification_log?: any
  retention_days: number
  expire_at?: string
  cleaned_at?: string
  created_at: string
  updated_at: string
  creator?: UserSummary
  assignee?: UserSummary
  // 兼容字段
  creator_name?: string
  assignee_name?: string
  // 文件存储相关字段（新）
  file_url?: string
  file_storage_id?: number
  translated_document_storage_id?: number
  /** 原文件名（后端可能返回 snake_case） */
  file_name?: string
}

// 文档上传响应
export interface DocumentUploadResponse extends FileUploadResponse {
  success: boolean
  message: string
}

// 文档项目列表响应
export interface DocumentProjectListResponse extends PaginatedResponse<DocumentProject> {
  total_pages: number
}

// 任务指派请求（条目创建时已确定目标语言，仅改被指派人）
export interface DocumentTaskAssignmentRequest {
  assignee_id: number
}

// 任务指派响应
export interface DocumentTaskAssignmentResponse {
  success: boolean
  message: string
  task_id: number
  work_item_id: number
}

export interface DocumentProjectQueryParams extends PaginationParams {
  /** @deprecated */
  project_type?: string
  /** @deprecated */
  status?: LegacyDocumentProjectStatus | string
  keyword?: string
  creator_id?: number
  assignee_id?: number
  date_range?: [string, string]
}

function deprecatedDocumentProjectCrud(name: string): never {
  throw new Error(
    `${name} 已废弃：请使用 projectApi.createProject + workItemApi.createWorkItem。`,
  )
}

/**
 * 上传文档到条目
 */
export const uploadDocumentToWorkItem = (
  workItemId: number,
  file: File,
): Promise<DocumentUploadResponse> => {
  const formData = new FormData()
  formData.append('file', file)

  return request.post(`/documents/work-items/${workItemId}/upload`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
}

/** @deprecated 请使用 uploadDocumentToWorkItem(workItemId, file) */
export const uploadDocumentToProject = (
  workItemId: number,
  file: File,
): Promise<DocumentUploadResponse> => uploadDocumentToWorkItem(workItemId, file)

/**
 * 删除条目文档文件
 */
export const deleteDocumentFile = (
  workItemId: number,
): Promise<{ success: boolean; message: string }> => {
  return request.delete(`/documents/work-items/${workItemId}/file`)
}

/**
 * @deprecated 请使用 createProject + createWorkItem，不要再走文档项目级 CRUD
 */
export const createDocumentProject = async (_data: {
  title: string
  document_type?: string
  original_language?: string
  target_languages?: string[]
  description?: string
}): Promise<DocumentProject> => {
  deprecatedDocumentProjectCrud('createDocumentProject')
}

/** @deprecated 请使用 getProjects / getWorkItems */
export const getDocumentProjects = (
  _params?: DocumentProjectQueryParams,
): Promise<DocumentProjectListResponse> => {
  deprecatedDocumentProjectCrud('getDocumentProjects')
}

/**
 * 获取文档条目详情（入参为 work_item_id）
 */
export const getDocumentProject = async (
  workItemId: number,
): Promise<DocumentProject> => {
  const item = await getWorkItem(workItemId)
  return {
    id: item.id,
    project_id: item.project_id,
    title: item.file_name ?? `#${item.id}`,
    document_type: item.file_type ?? 'document',
    target_languages: [item.target_language],
    original_language: item.source_language ?? undefined,
    status: item.status,
    creator_id: item.creator_id,
    file_name: item.file_name ?? undefined,
    file_url: item.file_url ?? undefined,
    file_storage_id: item.file_storage_id ?? undefined,
    translated_document_storage_id: item.translated_document_storage_id ?? undefined,
    translated_document_url: item.translated_document_url ?? undefined,
    document_url: item.file_url ?? undefined,
    created_at: item.created_at ?? '',
    updated_at: item.created_at ?? '',
    retention_days: 0,
  }
}

/** @deprecated */
export const updateDocumentProject = (
  _projectId: number,
  _data: Partial<DocumentProject>,
): Promise<DocumentProject> => {
  deprecatedDocumentProjectCrud('updateDocumentProject')
}

/** @deprecated */
export const deleteDocumentProject = (
  _projectId: number,
): Promise<{ success: boolean; message: string }> => {
  deprecatedDocumentProjectCrud('deleteDocumentProject')
}

/**
 * 指派文档校验任务（转调 work-items assign-review；旧 documents assign-task 仍兼容）
 */
export const assignDocumentTask = (
  workItemId: number,
  data: DocumentTaskAssignmentRequest,
): Promise<DocumentTaskAssignmentResponse> => {
  return request.post(`/work-items/${workItemId}/assign-review`, data)
}

/**
 * 提交当前校验 Task（submit 语义，不是「完成项目」）。
 * 完成项目请用 completeWorkItem。
 */
export const completeDocumentTranslation = (
  workItemId: number,
): Promise<{ message: string; status: string; work_item_id: number; task_id: number }> => {
  return request.post(`/documents/work-items/${workItemId}/complete-translation`)
}

// ===== 文档翻译相关API =====

/**
 * 开始文档翻译（条目级）
 */
export const startDocumentTranslation = (
  workItemId: number,
  targetLanguage: string,
  wait: boolean = true,
  maxWaitTime: number = 300,
): Promise<{
  success: boolean
  message: string
  task_id?: number
  translation_id?: string
  status?: string
  progress?: number
  waited?: boolean
  wait_time?: number
  timeout?: boolean
}> => {
  const params = new URLSearchParams({
    target_language: targetLanguage,
    wait: wait.toString(),
    max_wait_time: maxWaitTime.toString(),
  })
  return request.post(
    `/document-translation/work-items/${workItemId}/translate?${params.toString()}`,
  )
}

/**
 * 获取翻译进度（条目级）
 */
export const getTranslationProgress = async (
  workItemId: number,
): Promise<{
  progress: number
  status: string
  message?: string
}> => {
  try {
    return await request.get(
      `/document-translation/work-items/${workItemId}/translation-progress`,
    )
  } catch (error: unknown) {
    const err = error as { response?: { status?: number } }
    if (err?.response?.status === 404) {
      console.warn('翻译任务不存在，返回默认状态')
      return {
        progress: 0,
        status: 'pending',
        message: '翻译尚未开始',
      }
    }
    throw error
  }
}

/**
 * 保存文档内容（条目级）
 */
export const saveDocumentContent = (
  workItemId: number,
  content: unknown,
): Promise<{ success: boolean; message: string }> => {
  return request.post(`/document-translation/work-items/${workItemId}/save`, content)
}

/**
 * 获取文档内容（条目级）
 */
export const getDocumentContent = (workItemId: number): Promise<unknown> => {
  return request.get(`/document-translation/work-items/${workItemId}/content`)
}

/**
 * 跟踪编辑更改（条目级）
 */
export const trackEditingChanges = (
  workItemId: number,
  changes: unknown,
): Promise<{ success: boolean }> => {
  return request.post(
    `/document-translation/work-items/${workItemId}/track-changes`,
    changes,
  )
}

/**
 * 获取编辑历史（条目级）
 */
export const getEditingHistory = (workItemId: number): Promise<unknown[]> => {
  return request.get(`/document-translation/work-items/${workItemId}/editing-history`)
}

/**
 * 检查译文文档是否存在
 */
export const checkTranslatedDocumentExists = async (
  projectId: number,
  _targetLanguage?: string
): Promise<boolean> => {
  try {
    const project = await getDocumentProject(projectId)
    // 检查新字段（OSS模式）和旧字段（兼容模式）
    return !!(project.translated_document_storage_id || project.translated_document_url)
  } catch (error) {
    console.error('检查译文文档是否存在失败:', error)
    return false
  }
}

/**
 * 获取文件预览 URL（本地走 /api/v1/files/{id}/proxy，OSS 仍用签名直链）
 */
export const getFilePreviewUrl = async (fileStorageId: number): Promise<string> => {
  const baseUrl = getCorrectedApiOrigin() || window.location.origin
  try {
    const response = await request.get<{ file_id: number; url: string }>(
      `/files/${fileStorageId}/url`,
      { params: { expires_in: 3600 } }
    )
    const url = response.url
    if (
      url &&
      (url.startsWith('http://') || url.startsWith('https://')) &&
      (url.includes('aliyuncs.com') || url.includes('oss-'))
    ) {
      return url
    }
  } catch (error) {
    console.warn('⚠️ 获取 OSS 直链失败，回退 proxy:', error)
  }
  return `${baseUrl}${fileProxyUrl(fileStorageId)}`
}

/**
 * 获取文件直接访问URL（用于vue-office预览）
 * @deprecated 优先使用 getFilePreviewUrl
 */
export const getFileDirectUrl = async (fileStorageId: number): Promise<string> => {
  return getFilePreviewUrl(fileStorageId)
}

/**
 * 获取文件信息（包含文件类型）
 */
const getFileInfo = async (fileStorageId: number): Promise<{ url: string; fileType: string }> => {
  try {
    // 获取文件URL
    const url = await getFileDirectUrl(fileStorageId)
    
    // 路径形式（/document/.../xxx.docx）带扩展名，走 else 从 URL 取类型；proxy 格式无扩展名则请求文件信息 API
    let fileType = ''
    if (url.includes('/files/') && url.includes('/proxy')) {
      // 从URL中提取file_id
      const match = url.match(/\/files\/(\d+)\/proxy/)
      if (match) {
        const fileId = parseInt(match[1])
        try {
          // 获取文件信息
          const fileInfo = await request.get<{ original_name?: string }>(`/files/${fileId}`)
          if (fileInfo.original_name) {
            // 从文件名提取扩展名
            const extMatch = fileInfo.original_name.match(/\.([a-z0-9]+)$/i)
            if (extMatch) {
              fileType = extMatch[1].toLowerCase()
            }
          }
        } catch (error) {
          console.warn('⚠️ 获取文件信息失败:', error)
        }
      }
    } else {
      // 从URL中提取文件类型
      const match = url.match(/\.([a-z0-9]+)(?:\?|$)/i)
      if (match) {
        fileType = match[1].toLowerCase()
      }
    }
    
    return { url, fileType }
  } catch (error) {
    console.error('⚠️ 获取文件信息失败:', error)
    throw error
  }
}

/**
 * 获取原始文档URL（用于vue-office预览）
 * 直接使用OSS URL，不使用代理
 */
export const getDocumentUrl = async (
  projectId: number,
  docType: 'source' | 'target',
  _targetLanguage?: string
): Promise<string> => {
  // 先获取项目信息
  const project = await getDocumentProject(projectId)
  const baseUrl = getCorrectedApiOrigin() || window.location.origin

  if (docType === 'source') {
    // 原文：优先使用 file_storage_id，获取直接访问URL
    if (project.file_storage_id) {
      try {
        // 获取文件的直接访问URL（OSS签名URL或本地路径）
        const directUrl = await getFileDirectUrl(project.file_storage_id)
        return directUrl
      } catch (error) {
        // 获取文件直接URL失败，尝试使用 file_url
      }
    }
    
    // 回退到 file_url 或 document_url
    const sourceUrl = project.file_url || project.document_url
    if (sourceUrl) {
      // 如果是 OSS URL 或完整 URL，直接使用
      if (sourceUrl.includes('aliyuncs.com') || sourceUrl.includes('oss-') || sourceUrl.startsWith('http://') || sourceUrl.startsWith('https://')) {
        return sourceUrl
      }
      const normalized = normalizeLegacyFileUrl(
        sourceUrl.startsWith('/') ? sourceUrl : `/${sourceUrl}`
      )
      return `${baseUrl}${normalized.startsWith('/') ? '' : '/'}${normalized}`
    }
  } else {
    // 译文：优先使用 translated_document_storage_id，获取直接访问URL
    if (project.translated_document_storage_id) {
      try {
        // 获取文件的直接访问URL（OSS签名URL或本地路径）
        const directUrl = await getFileDirectUrl(project.translated_document_storage_id)
        return directUrl
      } catch (error) {
        // 获取文件直接URL失败，尝试使用 translated_document_url
      }
    }
    
    // 回退到 translated_document_url
    if (project.translated_document_url) {
      // 如果是 OSS URL 或完整 URL，直接使用
      if (project.translated_document_url.includes('aliyuncs.com') || project.translated_document_url.includes('oss-') || project.translated_document_url.startsWith('http://') || project.translated_document_url.startsWith('https://')) {
        return project.translated_document_url
      }
      const normalized = normalizeLegacyFileUrl(
        project.translated_document_url.startsWith('/')
          ? project.translated_document_url
          : `/${project.translated_document_url}`
      )
      return `${baseUrl}${normalized.startsWith('/') ? '' : '/'}${normalized}`
    }
  }
  
  throw new Error(`无法获取${docType === 'source' ? '原文' : '译文'}文档URL`)
}

/**
 * 获取文档URL和文件类型（用于vue-office预览）
 */
export const getDocumentUrlWithType = async (
  projectId: number,
  docType: 'source' | 'target',
  _targetLanguage?: string
): Promise<{ url: string; fileType: string }> => {
  // 先获取项目信息
  const project = await getDocumentProject(projectId)
  
  if (docType === 'source') {
    // 原文：优先使用 file_storage_id
    if (project.file_storage_id) {
      try {
        return await getFileInfo(project.file_storage_id)
      } catch (error) {
        // 获取文件信息失败，尝试使用 file_url
      }
    }
    
    // 回退到 file_url 或 document_url
    const sourceUrl = project.file_url || project.document_url
    if (sourceUrl) {
      const baseUrl = getCorrectedApiOrigin() || window.location.origin
      let url = sourceUrl
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        const normalized = normalizeLegacyFileUrl(url.startsWith('/') ? url : `/${url}`)
        url = `${baseUrl}${normalized.startsWith('/') ? '' : '/'}${normalized}`
      }
      
      // 从URL提取文件类型
      const match = url.match(/\.([a-z0-9]+)(?:\?|$)/i)
      const fileType = match ? match[1].toLowerCase() : ''
      
      return { url, fileType }
    }
  } else {
    // 译文：优先使用 translated_document_storage_id
    if (project.translated_document_storage_id) {
      try {
        return await getFileInfo(project.translated_document_storage_id)
      } catch (error) {
        // 获取文件信息失败，尝试使用 translated_document_url
      }
    }
    
    // 回退到 translated_document_url
    if (project.translated_document_url) {
      const baseUrl = getCorrectedApiOrigin() || window.location.origin
      let url = project.translated_document_url
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        const normalized = normalizeLegacyFileUrl(url.startsWith('/') ? url : `/${url}`)
        url = `${baseUrl}${normalized.startsWith('/') ? '' : '/'}${normalized}`
      }
      
      // 从URL提取文件类型
      const match = url.match(/\.([a-z0-9]+)(?:\?|$)/i)
      const fileType = match ? match[1].toLowerCase() : ''
      
      return { url, fileType }
    }
  }
  
  throw new Error(`无法获取${docType === 'source' ? '原文' : '译文'}文档URL`)
}

/**
 * 获取文档 PDF 预览 URL（整页预览用，替代 vue-office）
 * 后端使用 LibreOffice 转 PDF，前端用此 URL 展示 PDF。
 * 与 Axios baseURL 一致：若配置了 VITE_API_BASE_URL 则用完整 URL（PDF 请求与其它 API 打同一后端），
 * 否则用相对路径（走当前页同源/代理），避免 PDF 请求打到 Vite 代理而其它 API 打到远程后端导致日志对不上。
 */
export const getDocumentPdfPreviewUrl = (
  workItemId: number,
  docType: 'source' | 'target',
  targetLanguage?: string,
  forceRegenerate = false,
): string => {
  const base = getCorrectedApiOrigin()
  const params = new URLSearchParams()
  params.set('doc_type', docType)
  if (targetLanguage) params.set('target_language', targetLanguage)
  if (forceRegenerate) params.set('force_regenerate', 'true')
  const path = `/api/v1/document-translation/work-items/${workItemId}/pdf-preview?${params.toString()}`
  if (base) {
    return `${base.replace(/\/$/, '')}${path}`
  }
  return path
}

export const segmentDocuments = (workItemId: number): Promise<SegmentResponse> => {
  return request.post(`/document-translation/work-items/${workItemId}/segment`)
}

export const getSentences = (
  workItemId: number,
  params?: { page?: number; limit?: number },
): Promise<SentencesResponse> => {
  return request.get(`/document-translation/work-items/${workItemId}/sentences`, {
    params: params || {},
  })
}

export const updateSentences = (
  workItemId: number,
  updates: SentenceUpdate[],
): Promise<{ success: boolean; updated_count: number }> => {
  return request.post(
    `/document-translation/work-items/${workItemId}/sentences/update`,
    updates,
  )
}

export const syncToPreview = (
  workItemId: number,
  targetLanguage: string,
): Promise<SyncToPreviewResponse> => {
  return request.post(`/document-translation/work-items/${workItemId}/sync-to-preview`, null, {
    params: { target_language: targetLanguage },
  })
}

export const exportTranslatedDocument = (
  workItemId: number,
  format: 'docx' | 'pdf' = 'docx',
): Promise<ExportResponse> => {
  return request.get(`/document-translation/work-items/${workItemId}/export`, {
    params: { export_format: format },
  })
}

/**
 * 下载翻译后的文档（通过文件ID）
 * @param fileId 文件存储ID
 * @returns Axios响应（包含Blob数据）
 */
export const downloadTranslatedDocument = (fileId: number): Promise<any> => {
  // 只传入路径部分，让 request 自动拼接 baseURL
  return request.get(`/files/${fileId}/download`, {
    responseType: 'blob'
  })
}

// ===== 统一的文档API对象 =====
export const documentApi = {
  uploadDocumentToWorkItem,
  uploadDocumentToProject,
  deleteDocumentFile,
  createDocumentProject,
  getDocumentProjects,
  getDocumentProject,
  updateDocumentProject,
  assignDocumentTask,
  completeDocumentTranslation,
  deleteDocumentProject,
  
  // 文档翻译操作
  translateDocument: startDocumentTranslation,
  startDocumentTranslation,
  getTranslationProgress,
  saveDocumentContent,
  getDocumentContent,
  trackEditingChanges,
  getEditingHistory,
  
  // 文档翻译扩展操作
  getDocumentUrl,
  getDocumentPdfPreviewUrl,
  segmentDocuments,
  getSentences,
  updateSentences,
  syncToPreview,
  exportTranslatedDocument,
  downloadTranslatedDocument
}

